import { Alert, Button, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { omit } from "lodash";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { UserContextUserData } from "~/modules/auth/context/UserContext";
import AddressAutocompleteField from "~/modules/map/components/AddressAutocompleteField";
import useFieldworkerFinishOnboarding, { FieldworkerOnboardingParams } from "~/modules/user/api/useFieldworkerFinishOnboarding";

type Props = {
    user: UserContextUserData;
};

export default function FieldworkerOnboardingForm({ user }: Props) {
    const fieldworker = user.fieldworker;

    if (!fieldworker) {
        throw Error("Fieldworker is undefined");
    }

    const { t } = useTranslation("user");
    const { fieldworkerFinishOnboarding, error } = useFieldworkerFinishOnboarding();

    const {
        handleSubmit,
        control,
        setError,
        setValue,
        formState: { errors, isSubmitting, isLoading, isDirty, isValid, isSubmitted },
    } = useForm<FieldworkerOnboardingParams>({
        mode: "onBlur",
        defaultValues: {
            addressId: "",
            hasOwnTransport: false,
            hasDriversLicense: false,
            is21YearsOrOlder: false,
            hasDriversLicenseForMoreThan1Year: false,
        },
    });

    const onSubmit: SubmitHandler<FieldworkerOnboardingParams> = async data => {
        // If the user didn't select a new address, omit addressId so the backend keeps the existing one
        const payload = data.addressId === "" ? omit(data, "addressId") : data;
        await fieldworkerFinishOnboarding(payload);
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}
                <Controller
                    name="addressId"
                    control={control}
                    rules={{
                        required: !fieldworker.address,
                    }}
                    render={({ field }) => (
                        <AddressAutocompleteField
                            fieldLabel={t("fieldworkerOnboardingForm.labelAddress")}
                            noOptionsText={t("fieldworkerOnboardingForm.labelAddressNoOptions")}
                            onSelect={value =>
                                setValue("addressId", value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                })
                            }
                            initialValue={fieldworker.address}
                            {...field}
                            disabled={fieldworker.address !== null}
                        />
                    )}
                />
                <FormControlLabel
                    label={t("fieldworkerOnboardingForm.labelHaveACar")}
                    control={
                        <Controller
                            name="hasOwnTransport"
                            control={control}
                            render={({ field }) => <Switch {...field} checked={!!field.value} onChange={field.onChange} />}
                        />
                    }
                />
                <FormControlLabel
                    label={t("fieldworkerOnboardingForm.labelHasADriversLicense")}
                    control={
                        <Controller
                            name="hasDriversLicense"
                            control={control}
                            render={({ field }) => <Switch {...field} checked={!!field.value} onChange={field.onChange} />}
                        />
                    }
                />
                <FormControlLabel
                    label={t("fieldworkerOnboardingForm.label21yearsOrOlder")}
                    control={
                        <Controller
                            name="is21YearsOrOlder"
                            control={control}
                            render={({ field }) => <Switch {...field} checked={!!field.value} onChange={field.onChange} />}
                        />
                    }
                />
                <FormControlLabel
                    label={t("fieldworkerOnboardingForm.labelHasDriversLicenseMoreThan1Year")}
                    control={
                        <Controller
                            name="hasDriversLicenseForMoreThan1Year"
                            control={control}
                            render={({ field }) => <Switch {...field} checked={!!field.value} onChange={field.onChange} />}
                        />
                    }
                />
                <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isValid || !user.profilePictureMedia}
                    loading={isLoading}
                >
                    {t("fieldworkerOnboardingForm.btnSubmit")}
                </Button>
            </Stack>
        </form>
    );
}
